import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Upload,
  Space,
  Switch,
  Tag
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { fontAssets, upload, proxyImageUrl } from '../../api';

function FontAssetManage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);

    try {
      const res = await fontAssets.list({ include_disabled: 1 });
      setData(res.data.data || []);
    } catch (err) {
      message.error('获取字体列表失败');
    }

    setLoading(false);
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ sort_order: 0, enabled: true });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await fontAssets.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleEnabledChange = async (record, enabled) => {
    try {
      await fontAssets.update(record.id, {
        ...record,
        enabled
      });
      message.success(enabled ? '已启用' : '已停用');
      fetchData();
    } catch (err) {
      message.error('状态更新失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingItem) {
        await fontAssets.update(editingItem.id, values);
        message.success('更新成功');
      } else {
        await fontAssets.create(values);
        message.success('添加成功');
      }

      setModalVisible(false);
      fetchData();
    } catch (err) {
      if (err?.errorFields) {
        return;
      }

      message.error(err.response?.data?.message || '操作失败');
    }
  };

  const handleUpload = async (file) => {
    try {
      const res = await upload.upload(file);
      const nextUrl = res.data.data.url;
      const defaultName = file.name.replace(/\.[^.]+$/, '');

      form.setFieldsValue({
        file_url: nextUrl,
        name: form.getFieldValue('name') || defaultName,
        font_family: form.getFieldValue('font_family') || defaultName
      });
      message.success('上传成功');
    } catch (err) {
      message.error('上传失败');
    }

    return false;
  };

  return (
    <div>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card>
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>字体库</div>
            <div style={{ color: '#667085' }}>
              后台上传常用字体文件，前台编辑器会自动加载并出现在字体选择里。
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ width: 160 }}>
              添加字体
            </Button>
          </Space>
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
          {data.map((item) => (
            <Card
              key={item.id}
              loading={loading}
              hoverable
              actions={[
                <Button key="edit" type="link" icon={<EditOutlined />} onClick={() => handleEdit(item)}>
                  编辑
                </Button>,
                <Popconfirm key="delete" title="确定删除这个字体？" onConfirm={() => handleDelete(item.id)}>
                  <Button type="link" danger icon={<DeleteOutlined />}>
                    删除
                  </Button>
                </Popconfirm>
              ]}
            >
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div
                  style={{
                    padding: '18px 16px',
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
                    border: '1px solid #e5e7eb',
                    fontFamily: item.font_family,
                    fontSize: 30,
                    lineHeight: 1.2
                  }}
                >
                  瑞华海报
                </div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>{item.name}</div>
                <div style={{ color: '#667085' }}>font-family: {item.font_family}</div>
                <div>
                  <Tag color={item.enabled === false ? 'default' : 'success'}>
                    {item.enabled === false ? '已停用' : '已启用'}
                  </Tag>
                </div>
                <Switch
                  checked={item.enabled !== false}
                  checkedChildren="启用"
                  unCheckedChildren="停用"
                  onChange={(checked) => handleEnabledChange(item, checked)}
                />
                <div style={{ color: '#98a2b3', wordBreak: 'break-all' }}>
                  <a href={proxyImageUrl(item.file_url)} target="_blank" rel="noreferrer">
                    {item.file_url}
                  </a>
                </div>
              </Space>
            </Card>
          ))}
        </div>

        {!data.length && !loading && (
          <Card>
            <div style={{ textAlign: 'center', color: '#98a2b3', padding: '36px 0' }}>还没有字体，先上传几个常用字体文件。</div>
          </Card>
        )}
      </Space>

      <Modal
        title={editingItem ? '编辑字体' : '添加字体'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={680}
        maskClosable={false}
        keyboard={false}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="字体名称" rules={[{ required: true, message: '请输入字体名称' }]}>
            <Input placeholder="例如：思源黑体 Medium" />
          </Form.Item>

          <Form.Item name="font_family" label="字体族名称" rules={[{ required: true, message: '请输入字体族名称' }]}>
            <Input placeholder="例如：Source Han Sans SC Medium" />
          </Form.Item>

          <Form.Item name="sort_order" label="排序值">
            <Input type="number" placeholder="数值越大越靠前" />
          </Form.Item>

          <Form.Item name="enabled" label="前台显示" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>

          <Form.Item name="file_url" label="字体文件地址" rules={[{ required: true, message: '请上传字体文件或填写地址' }]}>
            <Input
              placeholder="https://example.com/font.woff2"
              addonBefore={
                <Upload showUploadList={false} beforeUpload={handleUpload} accept=".ttf,.otf,.woff,.woff2">
                  <Button icon={<UploadOutlined />}>上传</Button>
                </Upload>
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default FontAssetManage;
